import { useState } from "react";
import { Video } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { ChartCard } from "@/components/waste/reports/chart-card";
import { SegmentedToggle } from "@/components/waste/reports/segmented-toggle";
import type { Lens } from "@/hooks/use-lens";
import type { WasteLineStats } from "@/hooks/use-line-stats";
import { useTranslation } from "@/i18n/use-translation";
import { formatCurrency, formatWeight } from "@/lib/format";
import { foodLabel } from "@/lib/food-labels";
import {
  selectExamineLines,
  examineTrigger,
  type ExamineMode,
  type ExamineRow,
} from "@/lib/examine";

type Props = {
  /** First line-stats load — show a skeleton, not the empty state. */
  isLoading: boolean;
  lines: WasteLineStats[] | undefined;
  lens: Lens;
  currency: string;
  /** Narrows The Proof feed to the row's line (same wiring as Pulse / What's-working). */
  onViewEvidence: (row: ExamineRow) => void;
};

/**
 * "Examine waste" (design §10, position 8, POS2-876): which dish / which
 * purchase line — the step from insight to a par or order change. By dish
 * (default) lists recipe- and category-backed lines; By ingredient lists
 * inventory-item lines. The toggle is local state, so it survives lens and
 * period changes (the panel never unmounts). No emojis — catalogue items and
 * dishes carry no emoji field.
 */
export function ExamineWaste({ isLoading, lines, lens, currency, onViewEvidence }: Props) {
  const { locale, t } = useTranslation();
  const [mode, setMode] = useState<ExamineMode>("dish");
  if (isLoading && lines == null) return <ExamineSkeleton />;

  const dishList = selectExamineLines(lines ?? [], "dish", lens);
  const ingredientList = selectExamineLines(lines ?? [], "ingredient", lens);
  const { rows, total } = mode === "dish" ? dishList : ingredientList;
  const trigger = examineTrigger(t, lines ?? [], mode, lens);
  const fmt = (v: number) =>
    lens === "weight" ? formatWeight(locale, v) : formatCurrency(locale, v, currency);

  // Reserve the taller mode's row area (36px rows + 1px dividers) so the
  // dish/ingredient toggle doesn't make the panel jump.
  const maxRows = Math.max(dishList.rows.length, ingredientList.rows.length);
  const rowAreaMinHeight = maxRows * 36 + (maxRows - 1);

  return (
    <ChartCard
      title={t("dashboard.widget.examine")}
      description={t("dashboard.examineDescription")}
      isEmpty={maxRows === 0}
      emptyLabel={t("dashboard.examineEmpty")}
      action={
        <SegmentedToggle
          ariaLabel={t("dashboard.examineMode")}
          value={mode}
          onChange={setMode}
          options={[
            { value: "dish", label: t("dashboard.examineByDish") },
            { value: "ingredient", label: t("dashboard.examineByIngredient") },
          ]}
        />
      }
    >
      <div data-testid="examine-waste">
        {rows.length === 0 ? (
          <div
            className="flex items-center justify-center text-sm text-gray-400"
            style={{ minHeight: rowAreaMinHeight }}
            data-testid="examine-mode-empty"
          >
            {mode === "dish"
              ? t("dashboard.examineNoDishLines")
              : t("dashboard.examineNoIngredientLines")}
          </div>
        ) : (
          <>
            {/* Hairline-separated rows per the mock's .actrow. */}
            <div
              className="divide-y divide-gray-100"
              style={{ minHeight: rowAreaMinHeight }}
              data-testid="examine-rows"
            >
              {rows.map((row) => (
                <div
                  key={row.lineKey}
                  className="flex h-9 items-center justify-between gap-3"
                  data-testid="examine-row"
                >
                  <span
                    className="min-w-0 truncate text-sm text-gray-700"
                    title={foodLabel(t, row.label)}
                  >
                    {foodLabel(t, row.label)}
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    {row.hasEvidence && (
                      <button
                        type="button"
                        onClick={() => onViewEvidence(row)}
                        aria-label={t("dashboard.viewEvidence", { label: foodLabel(t, row.label) })}
                        className="rounded-md p-1 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                        data-testid="examine-evidence"
                      >
                        <Video className="size-4" aria-hidden />
                      </button>
                    )}
                    <span className="whitespace-nowrap font-display text-sm font-bold tabular-nums text-gray-900">
                      {fmt(row.value)}
                    </span>
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-0.5 flex items-baseline justify-between border-t border-gray-100 pt-2.5 text-xs text-gray-500">
              <span>
                {mode === "dish"
                  ? t("dashboard.examineTopDish")
                  : t("dashboard.examineTopIngredient")}
              </span>
              <span data-testid="examine-total">
                <b className="font-semibold text-gray-900">{fmt(total)}</b>{" "}
                {t("dashboard.examineTotalSuffix")}
              </span>
            </div>
            {trigger && (
              <p
                className="mt-3 border-t border-gray-100 pt-2.5 text-[10.5px] uppercase tracking-wide text-gray-400"
                data-testid="examine-trigger"
              >
                {trigger}
              </p>
            )}
          </>
        )}
      </div>
    </ChartCard>
  );
}

function ExamineSkeleton() {
  return (
    <div
      className="rounded-xl border border-gray-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(17,24,39,0.04),0_1px_3px_rgba(17,24,39,0.06)]"
      data-testid="examine-skeleton"
    >
      <Skeleton className="h-4 w-1/3" />
      <div className="mt-4 flex flex-col gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between gap-3">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-14" />
          </div>
        ))}
      </div>
    </div>
  );
}
